// ✨ EffectsManager - Handles all visual effects, particles, and animations
// Creates jump effects, collection effects, damage effects, and particle systems

export default class EffectsManager {
  constructor(scene) {
    this.scene = scene;
  }

  // === PLAYER EFFECTS ===

  createJumpEffect(x, y) {
    // Simple dust particles when jumping
    for (let i = 0; i < 5; i++) {
      const dust = this.scene.add.circle(
        x + (Math.random() - 0.5) * 20,
        y + 20,
        3,
        0xD2B48C
      );
      
      this.scene.tweens.add({
        targets: dust,
        alpha: 0,
        y: dust.y + 20,
        duration: 500,
        onComplete: () => {
          if (dust && dust.active) {
            dust.destroy();
          }
        }
      });
    }
  }

  createDoubleJumpEffect(x, y) {
    // Special effect for double jump with sparkles
    for (let i = 0; i < 8; i++) {
      const spark = this.scene.add.circle(
        x + (Math.random() - 0.5) * 30,
        y + 10,
        3,
        0xFFD700
      );
      
      this.scene.tweens.add({
        targets: spark,
        alpha: 0,
        y: spark.y + 30,
        scale: 2,
        duration: 800,
        onComplete: () => {
          if (spark && spark.active) {
            spark.destroy();
          }
        }
      });
    }
  }

  createDamageEffect(x, y) {
    // Screen flash for damage
    this.scene.cameras.main.flash(200, 255, 0, 0);
    
    // Damage text
    const damageText = this.scene.add.text(x, y - 30, 'OUCH!', {
      fontSize: '12px',
      fontFamily: 'Press Start 2P',
      color: '#FF0000',
      stroke: '#FFFFFF',
      strokeThickness: 2
    }).setOrigin(0.5);
    
    this.scene.tweens.add({
      targets: damageText,
      y: damageText.y - 30,
      alpha: 0,
      duration: 1000,
      onComplete: () => {
        if (damageText && damageText.active) {
          damageText.destroy();
        }
      }
    });

    // Damage particles
    for (let i = 0; i < 6; i++) {
      const particle = this.scene.add.circle(
        x + (Math.random() - 0.5) * 30,
        y + (Math.random() - 0.5) * 20,
        2,
        0xFF0000
      );
      
      this.scene.tweens.add({
        targets: particle,
        alpha: 0,
        scale: 0,
        duration: 800,
        onComplete: () => {
          if (particle && particle.active) {
            particle.destroy();
          }
        }
      });
    }
  }

  // === COLLECTION EFFECTS ===

  createCollectionEffect(x, y, points) {
    // Points text with enhanced styling
    const pointsText = this.scene.add.text(x, y, `+${points}`, {
      fontSize: '16px',
      fontFamily: 'Press Start 2P',
      color: '#FFD700',
      stroke: '#000000',
      strokeThickness: 2
    }).setOrigin(0.5);
    
    this.scene.tweens.add({
      targets: pointsText,
      y: y - 50,
      alpha: 0,
      scale: 1.2,
      duration: 1000,
      onComplete: () => {
        if (pointsText && pointsText.active) {
          pointsText.destroy();
        }
      }
    });
    
    // Sparkle particles
    for (let i = 0; i < 8; i++) {
      const sparkle = this.scene.add.circle(
        x + (Math.random() - 0.5) * 40,
        y + (Math.random() - 0.5) * 40,
        2,
        Phaser.Utils.Array.GetRandom([0xFFD700, 0xFF69B4, 0x4ECDC4])
      );
      
      this.scene.tweens.add({
        targets: sparkle,
        alpha: 0,
        scale: 2,
        rotation: Math.PI * 2,
        duration: 800,
        onComplete: () => {
          if (sparkle && sparkle.active) {
            sparkle.destroy();
          }
        }
      });
    }
  }

  createHeartBoxEffect(x, y) {
    // Create heart particles explosion
    for (let i = 0; i < 5; i++) {
      const heart = this.scene.add.image(x, y, 'heart');
      heart.setScale(2);
      heart.setTint(0xFF1493);
      
      // Heart explosion animation
      this.scene.tweens.add({
        targets: heart,
        x: x + (Math.random() - 0.5) * 100,
        y: y - 50 - (Math.random() * 50),
        scaleX: 0.5,
        scaleY: 0.5,
        alpha: 0,
        rotation: Math.random() * Math.PI * 2,
        duration: 1000,
        ease: 'Quad.easeOut',
        onComplete: () => {
          if (heart && heart.active) {
            heart.destroy();
          }
        }
      });
    }

    // Bonus text with special styling
    const bonusText = this.scene.add.text(x, y - 30, 'HEART BONUS!', {
      fontSize: '14px',
      fontFamily: 'Press Start 2P',
      color: '#FF1493',
      stroke: '#FFFFFF',
      strokeThickness: 2
    }).setOrigin(0.5);
    
    this.scene.tweens.add({
      targets: bonusText,
      y: bonusText.y - 40,
      alpha: 0,
      scale: 1.3,
      duration: 1500,
      onComplete: () => {
        if (bonusText && bonusText.active) {
          bonusText.destroy();
        }
      }
    });
  }

  // === NPC INTERACTION EFFECTS ===

  createNPCInteractionEffect(x, y, npcType) {
    const messages = {
      'nova-cat': 'MEOW!',
      'linus-cat': 'HISS!',
      'miles': 'OOPS!'
    };
    
    const message = this.scene.add.text(x, y - 20, messages[npcType] || 'BUMP!', {
      fontSize: '10px',
      fontFamily: 'Press Start 2P',
      color: '#FF6347',
      stroke: '#FFFFFF',
      strokeThickness: 2
    }).setOrigin(0.5);
    
    this.scene.tweens.add({
      targets: message,
      y: message.y - 30,
      alpha: 0,
      duration: 1500,
      onComplete: () => {
        if (message && message.active) {
          message.destroy();
        }
      }
    });

    // Impact stars
    for (let i = 0; i < 4; i++) {
      const star = this.scene.add.image(x, y, 'star');
      star.setScale(1);
      star.setTint(0xFFD700);
      
      this.scene.tweens.add({
        targets: star,
        x: x + (Math.random() - 0.5) * 60,
        y: y - 20 - (Math.random() * 30),
        alpha: 0,
        rotation: Math.PI * 2,
        duration: 1200,
        onComplete: () => {
          if (star && star.active) {
            star.destroy();
          }
        }
      });
    }
  }

  // === EXPLOSION EFFECTS ===

  createExplosionEffect(x, y) {
    // Create explosion particles
    for (let i = 0; i < 12; i++) {
      const particle = this.scene.add.circle(
        x,
        y,
        3 + Math.random() * 3,
        Phaser.Utils.Array.GetRandom([0xFF4500, 0xFF6347, 0xFFD700, 0xFFA500])
      );
      
      const angle = (i / 12) * Math.PI * 2;
      const speed = 50 + Math.random() * 50;
      
      this.scene.tweens.add({
        targets: particle,
        x: x + Math.cos(angle) * speed,
        y: y + Math.sin(angle) * speed,
        alpha: 0,
        scale: 0,
        duration: 800,
        onComplete: () => {
          if (particle && particle.active) {
            particle.destroy();
          }
        }
      });
    }

    // Explosion flash
    const flash = this.scene.add.circle(x, y, 30, 0xFFFFFF, 0.8);
    this.scene.tweens.add({
      targets: flash,
      scale: 3,
      alpha: 0,
      duration: 300,
      onComplete: () => {
        if (flash && flash.active) {
          flash.destroy();
        }
      }
    });
  }

  // === LEVEL COMPLETION EFFECTS ===

  createLevelCompleteEffect() {
    const { width, height } = this.scene.scale;
    
    // Celebration particles falling from top
    for (let i = 0; i < 20; i++) {
      this.scene.time.delayedCall(i * 100, () => {
        const isHeart = Math.random() > 0.5;
        const particle = this.scene.add.image(
          Math.random() * width,
          -20,
          isHeart ? 'heart' : 'star'
        ).setScale(2);
        
        this.scene.tweens.add({
          targets: particle,
          y: height + 20,
          x: particle.x + (Math.random() - 0.5) * 100,
          rotation: Math.PI * 2,
          duration: 3000,
          ease: 'Linear',
          onComplete: () => {
            if (particle && particle.active) {
              particle.destroy();
            }
          }
        });
      });
    }
  }

  // === POWER-UP EFFECTS ===

  createPowerUpEffect(x, y, powerType) {
    const colors = {
      'veil': 0xFFFFFF,
      'hat': 0x4169E1,
      'speed': 0x32CD32,
      'invincible': 0xFFD700
    };
    
    const color = colors[powerType] || 0xFFFFFF;
    
    // Power-up aura
    const aura = this.scene.add.circle(x, y, 20, color, 0.3);
    this.scene.tweens.add({
      targets: aura,
      scale: 2,
      alpha: 0,
      duration: 1000,
      onComplete: () => {
        if (aura && aura.active) {
          aura.destroy();
        }
      }
    });

    // Power-up particles
    for (let i = 0; i < 6; i++) {
      const particle = this.scene.add.circle(
        x + (Math.random() - 0.5) * 40,
        y + (Math.random() - 0.5) * 40,
        3,
        color
      );
      
      this.scene.tweens.add({
        targets: particle,
        alpha: 0,
        scale: 2,
        y: particle.y - 30,
        duration: 1500,
        onComplete: () => {
          if (particle && particle.active) {
            particle.destroy();
          }
        }
      });
    }
  }

  // === ENVIRONMENTAL EFFECTS ===

  createGroundImpactEffect(x, y) {
    // Dust cloud when landing hard
    for (let i = 0; i < 8; i++) {
      const dust = this.scene.add.circle(
        x + (Math.random() - 0.5) * 40,
        y,
        2 + Math.random() * 2,
        0xD2B48C,
        0.6
      );
      
      this.scene.tweens.add({
        targets: dust,
        alpha: 0,
        y: dust.y + 15,
        scale: 2,
        duration: 1000,
        onComplete: () => {
          if (dust && dust.active) {
            dust.destroy();
          }
        }
      });
    }
  }

  createSpeedLinesEffect(x, y) {
    // Speed lines for fast movement
    for (let i = 0; i < 5; i++) {
      const line = this.scene.add.rectangle(
        x - 30 - (i * 10),
        y + (Math.random() - 0.5) * 20,
        20,
        2,
        0xFFFFFF,
        0.7
      );
      
      this.scene.tweens.add({
        targets: line,
        alpha: 0,
        x: line.x - 50,
        duration: 300,
        onComplete: () => {
          if (line && line.active) {
            line.destroy();
          }
        }
      });
    }
  }

  // === UTILITY METHODS ===

  // Create a simple particle burst
  createParticleBurst(x, y, count = 8, color = 0xFFFFFF, size = 3) {
    for (let i = 0; i < count; i++) {
      const particle = this.scene.add.circle(
        x + (Math.random() - 0.5) * 20,
        y + (Math.random() - 0.5) * 20,
        size,
        color
      );
      
      this.scene.tweens.add({
        targets: particle,
        alpha: 0,
        scale: 0,
        duration: 800,
        onComplete: () => {
          if (particle && particle.active) {
            particle.destroy();
          }
        }
      });
    }
  }

  // Create floating text effect
  createFloatingText(x, y, text, style = {}) {
    const defaultStyle = {
      fontSize: '12px',
      fontFamily: 'Press Start 2P',
      color: '#FFFFFF',
      stroke: '#000000',
      strokeThickness: 1
    };
    
    const textObj = this.scene.add.text(x, y, text, { ...defaultStyle, ...style }).setOrigin(0.5);
    
    this.scene.tweens.add({
      targets: textObj,
      y: y - 40,
      alpha: 0,
      duration: 2000,
      onComplete: () => {
        if (textObj && textObj.active) {
          textObj.destroy();
        }
      }
    });
    
    return textObj;
  }
}